import { useCallback, useEffect, useRef, useState } from 'react';
import { isDashboardCardWidth, type DashboardCardWidth } from './dashboardGridItemClassName';
import { reorderDashboardOrder } from './reorderDashboardOrder';

// Stable default so `defaultHidden` doesn't become a new array reference every render.
const EMPTY_DEFAULT_HIDDEN: readonly string[] = [];

export type { DashboardCardWidth };

export interface DashboardLayoutState<TId extends string> {
  order: TId[];
  hidden: TId[];
  widths: Partial<Record<TId, DashboardCardWidth>>;
}

function isKnownId<TId extends string>(value: unknown, allowed: readonly TId[]): value is TId {
  return typeof value === 'string' && allowed.includes(value as TId);
}

function sanitizeDefaultHidden<TId extends string>(
  defaultOrder: readonly TId[],
  defaultHidden: readonly TId[]
): TId[] {
  return defaultHidden.filter((id, index, arr) => defaultOrder.includes(id) && arr.indexOf(id) === index);
}

export function normalizeDashboardLayout<TId extends string>(
  value: unknown,
  defaultOrder: readonly TId[],
  defaultHidden: readonly TId[] = [],
  getAllowedWidths?: (id: TId) => readonly DashboardCardWidth[] | undefined
): DashboardLayoutState<TId> {
  const freshHidden = sanitizeDefaultHidden(defaultOrder, defaultHidden);
  if (!value || typeof value !== 'object') {
    return { order: [...defaultOrder], hidden: freshHidden, widths: {} };
  }

  const raw = value as Partial<Record<keyof DashboardLayoutState<TId>, unknown>>;
  const incomingOrder = Array.isArray(raw.order)
    ? raw.order.filter((id): id is TId => isKnownId(id, defaultOrder))
    : [];
  const order = [
    ...incomingOrder.filter((id, index) => incomingOrder.indexOf(id) === index),
    ...defaultOrder.filter((id) => !incomingOrder.includes(id))
  ];
  const hidden = Array.isArray(raw.hidden)
    ? raw.hidden.filter((id): id is TId => isKnownId(id, defaultOrder)).filter((id, index, arr) => arr.indexOf(id) === index)
    : freshHidden;

  const widths: Partial<Record<TId, DashboardCardWidth>> = {};
  if (raw.widths && typeof raw.widths === 'object' && !Array.isArray(raw.widths)) {
    for (const [key, width] of Object.entries(raw.widths)) {
      if (isKnownId(key, defaultOrder) && isDashboardCardWidth(width)) {
        const allowed = getAllowedWidths?.(key);
        widths[key] = allowed && allowed.length > 0
          ? (allowed.includes(width) ? width : allowed[0])
          : width;
      }
    }
  }

  return { order, hidden, widths };
}

export function useDashboardLayout<TId extends string>(
  storageKeyPrefix: string,
  username: string | null,
  defaultOrder: readonly TId[],
  defaultHidden: readonly TId[] = EMPTY_DEFAULT_HIDDEN as readonly TId[],
  getAllowedWidths?: (id: TId) => readonly DashboardCardWidth[] | undefined
) {
  const storageKey = `${storageKeyPrefix}:${encodeURIComponent(username ?? 'user')}`;

  // Keep the latest defaults in a ref so `readLayout` stays referentially stable across renders.
  // If these were captured directly, a caller passing an inline `defaultOrder`/`defaultHidden`
  // array would make `readLayout` (and the effect below) change every render and setState in a
  // loop, which starves React's scheduler and freezes route transitions.
  const defaultsRef = useRef({ defaultOrder, defaultHidden, getAllowedWidths });
  defaultsRef.current = { defaultOrder, defaultHidden, getAllowedWidths };

  const readLayout = useCallback((): DashboardLayoutState<TId> => {
    const { defaultOrder: order, defaultHidden: hidden, getAllowedWidths: allowed } = defaultsRef.current;
    try {
      const raw = localStorage.getItem(storageKey);
      return raw
        ? normalizeDashboardLayout(JSON.parse(raw), order, hidden, allowed)
        : normalizeDashboardLayout(null, order, hidden, allowed);
    } catch {
      return normalizeDashboardLayout(null, order, hidden, allowed);
    }
  }, [storageKey]);

  const [layout, setLayout] = useState<DashboardLayoutState<TId>>(() => readLayout());

  useEffect(() => {
    setLayout(readLayout());
  }, [readLayout]);

  const persistLayout = useCallback(
    (next: DashboardLayoutState<TId>) => {
      setLayout(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // localStorage can be unavailable in private browsing.
      }
    },
    [storageKey]
  );

  const moveCard = useCallback(
    (id: TId, direction: -1 | 1) => {
      const index = layout.order.indexOf(id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= layout.order.length) return;
      const order = [...layout.order];
      [order[index], order[nextIndex]] = [order[nextIndex], order[index]];
      persistLayout({ ...layout, order });
    },
    [layout, persistLayout]
  );

  const toggleCard = useCallback(
    (id: TId) => {
      const nextHidden = new Set(layout.hidden);
      if (nextHidden.has(id)) nextHidden.delete(id);
      else nextHidden.add(id);
      persistLayout({ ...layout, hidden: [...nextHidden] });
    },
    [layout, persistLayout]
  );

  const resetLayout = useCallback(() => {
    persistLayout(
      normalizeDashboardLayout(null, defaultOrder, defaultHidden, getAllowedWidths)
    );
  }, [defaultHidden, defaultOrder, getAllowedWidths, persistLayout]);

  const setCardWidth = useCallback(
    (id: TId, width: DashboardCardWidth) => {
      const allowed = getAllowedWidths?.(id);
      if (allowed && allowed.length > 0 && !allowed.includes(width)) return;
      persistLayout({ ...layout, widths: { ...layout.widths, [id]: width } });
    },
    [getAllowedWidths, layout, persistLayout]
  );

  const reorderCards = useCallback(
    (fromIndex: number, toIndex: number, options?: { visibleOnly?: boolean }) => {
      const next = reorderDashboardOrder(layout, fromIndex, toIndex, options);
      const orderChanged = next.order.some((id, index) => id !== layout.order[index]);
      if (orderChanged) {
        persistLayout(next);
      }
    },
    [layout, persistLayout]
  );

  return {
    layout,
    hidden: new Set(layout.hidden),
    visibleOrder: layout.order.filter((id) => !layout.hidden.includes(id)),
    moveCard,
    toggleCard,
    resetLayout,
    reorderCards,
    setCardWidth
  };
}
